import axios, { AxiosRequestConfig } from "axios";
import { environment } from "../../environments/environment";
import { appInsights } from "../../core/services/insight.service";
import Cookies from "js-cookie";
import { User } from "@auth0/auth0-react";
import { Console } from "console";

const API_URL = environment.apiEndpoint;
interface FetchDataOptions {
  params?: Record<string, any>;
  headers?: Record<string, string>;
}

async function getCsrfToken(): Promise<string | undefined> {
  return Cookies.get("XSRF-TOKEN"); // Adjust cookie name if needed
}

export const fetchData = async <T>(
  encodedapi: string,
  userProfile?: User
): Promise<T> => {
  try {
    const headers = {
      "Content-Type": "application/json",
      ...(userProfile &&
        userProfile.email && {
          "encryptedcode": btoa(userProfile.email), // Base64 encode the email
        }),
    };
const BASE_URL = "https://"; // Allowed base URL

function validateEncodedApi(encodedapi: string): void {
  // Check for path traversal or invalid characters
  if (encodedapi.includes("..") || !/^[a-zA-Z0-9/_-]+$/.test(encodedapi)) {
    console.log("Invalid encodedapi:", encodedapi);
  }
}

function validateUrl(url: string): string {
  // Ensure the URL starts with the allowed base URL
  if (!url.startsWith(BASE_URL)) {
    throw new Error(`Invalid URL: URL must start with ${BASE_URL}`);
  }
  return url;
}

// Validate the `encodedapi` before making the request
validateEncodedApi(encodedapi);

// Construct and validate the full URL
const full = validateUrl(`${API_URL}/${encodedapi}`);

// Make the request
const response = await axios.get<T>(full, { headers });
return response.data;
  } catch (error: any) {
    appInsights.trackEvent({
      name: "FetchDataFailed",
      properties: {
        error: error.message || "An error occurred",
      },
    });
    throw error; // Re-throw the error for the caller to handle
  }
};

export const fetchDataWithParams = async <T>(
  endpoint: string,
  options?: FetchDataOptions
): Promise<T> => {
  try {
    const config: AxiosRequestConfig = {
      params: options?.params,
      headers: options?.headers,
    };

    const response = await axios.get<T>(`${API_URL}/${endpoint}`, config);
    return response.data;
  } catch (error: any) {
    appInsights.trackEvent({
      name: "FetchDataWithParamsFailed",
      properties: { error: error },
    });
    throw error;
  }
};
export const postData = async <T>(
  endpoint: string,
  data: T,
  userProfile?: User
): Promise<T> => {
  try {
    const response = await fetch(`${API_URL}/${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(userProfile &&
          userProfile.email && {
            "encryptedcode": btoa(userProfile.email), // Base64 encode the email
          }), // Base64 encode the email
      } as HeadersInit,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      return (await response.text()) as T;
    }
    const responseData: T = await response.json();
    return responseData;
  } catch (error: any) {
    appInsights.trackEvent({
      name: "PostDataFailed",
      properties: { error: error },
    });
    return error as T;
  }
};
export const postAttachData = async <T>(endpoint: string, data: T) => {
  try {
    const response = await axios.post(`${API_URL}/${endpoint}`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  } catch (error: any) {
    appInsights.trackEvent({
      name: "PostAttachDataFailed",
      properties: { error: error },
    });
    throw error;
  }
};

export const deleteData = async (
  endpoint: string,
  userProfile?: User
): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/${endpoint}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...(userProfile &&
          userProfile.email && {
            "encryptedcode": btoa(userProfile.email), // Base64 encode the email
          }),
      } as HeadersInit,
    });
    if (!response.ok) {
      throw new Error(`Error deleting data: ${response.statusText}`);
    }
  } catch (error: any) {
    console.error("Error deleting data:", error);
    appInsights.trackEvent({
      name: "DeleteDataFailed",
      properties: { error: error },
    });
    throw error;
  }
};
